import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';

import { auth } from '../firebase';
import { ensureUserDocuments } from '../lib/account';
import { errorCode, errorMessage } from '../lib/errors';
import AuthLayout, { Divider } from './AuthLayout';
import Field from './Field';
import GoogleButton from './GoogleButton';
import TopPuns from './TopPuns';

function Signup() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignup = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      await ensureUserDocuments(user, name);

      navigate('/today');
    } catch (err) {
      console.error('[Signup] could not create account:', err);
      const code = errorCode(err);

      if (code === 'auth/email-already-in-use') {
        setError('That email already has an account. Try signing in.');
      } else if (code === 'auth/weak-password') {
        setError('Passwords need at least six characters.');
      } else {
        setError(errorMessage(err) || 'Could not create the account. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Make an account."
      subtitle="Takes fifteen seconds. Roughly one pun's worth."
      aside={<TopPuns />}
    >
      {error && (
        <p className="mb-4 rounded-xl border-2 border-ink bg-coral px-4 py-3 text-sm font-medium text-white shadow-pop-sm">
          {error}
        </p>
      )}

      <GoogleButton onDone={() => navigate('/today')} onError={(message) => setError(message || null)} />

      <Divider />

      <form onSubmit={handleSignup} className="space-y-4">
        <Field
          label="Display name"
          type="text"
          value={name}
          onChange={setName}
          autoComplete="nickname"
          placeholder="What people will see"
        />
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
        />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          minLength={6}
          required
        />

        <button
          type="submit"
          disabled={loading}
          className="press w-full rounded-xl border-2 border-ink bg-mustard px-5 py-3 font-display text-lg font-bold shadow-pop disabled:cursor-not-allowed disabled:bg-ink/10 disabled:text-ink/30"
        >
          {loading ? 'Creating...' : 'Start punning'}
        </button>
      </form>

      <p className="mt-5 text-sm text-ink/60">
        Already have one?{' '}
        <Link to="/" className="font-semibold text-ink underline underline-offset-2">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}

export default Signup;
